<script setup lang="ts">
const localePath = useLocalePath()
const { t } = useI18n();

useHead({
  titleTemplate: () => `${t('cascadeApi.title')} | SMSBAT API`,
  meta: [
    { name: "description", content: () => t('cascadeApi.subtitle') },
    { property: "og:title", content: () => `${t('cascadeApi.title')} | SMSBAT API` },
    { property: "og:description", content: () => t('cascadeApi.subtitle') },
    { property: "og:image", content: "https://smsbat.com/images/smsbat-cascade-restful-api.png" },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary_large_image" }
  ]
})

onMounted(() => {
  const el = document.querySelector("body")
  el?.scrollIntoView()
  if (globalThis.location.hash) {
    const el = document.querySelector(globalThis.location.hash)
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth' })
      }, 500)
    }
  }
})

</script>

<template>
  <div class="smsbat-cascade-restful-api-page">
    <div class="main-banner">
      <div class="container">
        <div class="banner-content">
          <div class="banner-text">
            <h1 class="banner-title">
              {{ $t('cascadeApi.title') }}
            </h1>
            <p class="banner-description">
              {{ $t('cascadeApi.subtitle') }}
            </p>
            <div class="banner-action">
              <NuxtLink to="https://omni.smsbat.com/register-ca" external target="_blank"
                class="nav-link button filled whitespace-nowrap">
                {{ $t('cascadeApi.hero.getStarted') }}
              </NuxtLink>
              <NuxtLink to="https://developers.smsbat.com/" external target="_blank"
                class="button outlined whitespace-nowrap">
                {{ $t('cascadeApi.hero.viewDocs') }}
              </NuxtLink>
            </div>
          </div>
          <div class="banner-image-container">
          <NuxtImg src="/images/smsbat-cascade-restful-api.png" class="mt-20 w-full h-auto object-contain" alt="SMSBAT Cascade RESTful API" width="2668" height="2000" format="webp" loading="eager" fetchpriority="high" />
          </div>
        </div>
      </div>
    </div>

    <!-- Global Stats Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="stats-container">
          <div class="stats-title">
            {{ $t('cascadeApi.stats.title') }}
          </div>
          <div class="stats-subtitle">
            {{ $t('cascadeApi.stats.subtitle') }}
          </div>
          <div class="stats-grid">
            <div class="stat-item">
              <div class="stat-number">4+</div>
              <div class="stat-label">{{ $t('cascadeApi.stats.platforms') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">1</div>
              <div class="stat-label">{{ $t('cascadeApi.stats.request') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">99.9%</div>
              <div class="stat-label">{{ $t('cascadeApi.stats.reliability') }}</div>
            </div>
            <div class="stat-item">
              <div class="stat-number">24/7</div>
              <div class="stat-label">{{ $t('cascadeApi.stats.support') }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="section-title">
          {{ $t('cascadeApi.features.title') }}
        </div>
        <div class="features-grid">
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('cascadeApi.features.efficiency.title') }}</h3>
              <p>{{ $t('cascadeApi.features.efficiency.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 17L12 22L22 17" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <path d="M2 12L12 17L22 12" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('cascadeApi.features.reducedLoad.title') }}</h3>
              <p>{{ $t('cascadeApi.features.reducedLoad.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
                <circle cx="12" cy="12" r="10" stroke="#3070b6" stroke-width="2"></circle>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('cascadeApi.features.seamlessIntegration.title') }}</h3>
              <p>{{ $t('cascadeApi.features.seamlessIntegration.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22S2 16 2 10V5L12 2L22 5V10C22 16 12 22Z" stroke="#3070b6" stroke-width="2"
                  stroke-linecap="round" stroke-linejoin="round"></path>
                <path d="M9 12L11 14L15 10" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('cascadeApi.features.reliability.title') }}</h3>
              <p>{{ $t('cascadeApi.features.reliability.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="#3070b6" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('cascadeApi.features.performance.title') }}</h3>
              <p>{{ $t('cascadeApi.features.performance.description') }}</p>
            </div>
          </div>
          <div class="feature-item">
            <div class="feature-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="feature-content">
              <h3>{{ $t('cascadeApi.features.monitoring.title') }}</h3>
              <p>{{ $t('cascadeApi.features.monitoring.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Communication Channels Section -->
    <div class="page-section bg-gray-50">
      <div class="section-content">
        <div class="section-title">
          {{ $t('cascadeApi.channels.title') }}
        </div>
        <div class="channels-grid">
          <div class="channel-item">
            <div class="channel-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="channel-content">
              <h3>{{ $t('cascadeApi.channels.telegram.title') }}</h3>
              <p>{{ $t('cascadeApi.channels.telegram.description') }}</p>
            </div>
          </div>
          <div class="channel-item">
            <div class="channel-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="channel-content">
              <h3>{{ $t('cascadeApi.channels.viberBot.title') }}</h3>
              <p>{{ $t('cascadeApi.channels.viberBot.description') }}</p>
            </div>
          </div>
          <div class="channel-item">
            <div class="channel-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="channel-content">
              <h3>{{ $t('cascadeApi.channels.viberBusiness.title') }}</h3>
              <p>{{ $t('cascadeApi.channels.viberBusiness.description') }}</p>
            </div>
          </div>
          <div class="channel-item">
            <div class="channel-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="channel-content">
              <h3>{{ $t('cascadeApi.channels.sms.title') }}</h3>
              <p>{{ $t('cascadeApi.channels.sms.description') }}</p>
            </div>
          </div>
          <div class="channel-item">
            <div class="channel-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="channel-content">
              <h3>{{ $t('cascadeApi.channels.meta.title') }}</h3>
              <p>{{ $t('cascadeApi.channels.meta.description') }}</p>
            </div>
          </div>
          <div class="channel-item">
            <div class="channel-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M21 15C21 15.5304 20.7893 16.0391 20.4142 16.4142C20.0391 16.7893 19.5304 17 19 17H7L3 21V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V15Z"
                  stroke="#3070b6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>
              </svg>
            </div>
            <div class="channel-content">
              <h3>{{ $t('cascadeApi.channels.rcs.title') }}</h3>
              <p>{{ $t('cascadeApi.channels.rcs.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Business Benefits Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="section-title">
          {{ $t('cascadeApi.benefits.title') }}
        </div>
        <div class="benefits-grid">
          <div class="benefit-item">
            <div class="benefit-icon">💰</div>
            <div class="benefit-content">
              <h3>{{ $t('cascadeApi.benefits.costSavings.title') }}</h3>
              <p>{{ $t('cascadeApi.benefits.costSavings.description') }}</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">👥</div>
            <div class="benefit-content">
              <h3>{{ $t('cascadeApi.benefits.userExperience.title') }}</h3>
              <p>{{ $t('cascadeApi.benefits.userExperience.description') }}</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">📈</div>
            <div class="benefit-content">
              <h3>{{ $t('cascadeApi.benefits.scalability.title') }}</h3>
              <p>{{ $t('cascadeApi.benefits.scalability.description') }}</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">🔒</div>
            <div class="benefit-content">
              <h3>{{ $t('cascadeApi.benefits.security.title') }}</h3>
              <p>{{ $t('cascadeApi.benefits.security.description') }}</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">⚙️</div>
            <div class="benefit-content">
              <h3>{{ $t('cascadeApi.benefits.flexibility.title') }}</h3>
              <p>{{ $t('cascadeApi.benefits.flexibility.description') }}</p>
            </div>
          </div>
          <div class="benefit-item">
            <div class="benefit-icon">📊</div>
            <div class="benefit-content">
              <h3>{{ $t('cascadeApi.benefits.analytics.title') }}</h3>
              <p>{{ $t('cascadeApi.benefits.analytics.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- How It Works Section -->
    <div class="page-section bg-gray-50">
      <div class="section-content">
        <div class="section-title">
          {{ $t('cascadeApi.howItWorks.title') }}
        </div>
        <div class="how-it-works-content">
          <div class="step-item">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>{{ $t('cascadeApi.howItWorks.step1.title') }}</h3>
              <p>{{ $t('cascadeApi.howItWorks.step1.description') }}</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>{{ $t('cascadeApi.howItWorks.step2.title') }}</h3>
              <p>{{ $t('cascadeApi.howItWorks.step2.description') }}</p>
            </div>
          </div>
          <div class="step-item">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>{{ $t('cascadeApi.howItWorks.step3.title') }}</h3>
              <p>{{ $t('cascadeApi.howItWorks.step3.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Advanced Features Section -->
    <div class="page-section">
      <div class="section-content">
        <div class="section-title">
          {{ $t('cascadeApi.advancedFeatures.title') }}
        </div>
        <div class="advanced-features-grid">
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.trafficMonitoring.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.trafficMonitoring.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.sessionTracking.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.sessionTracking.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.apiReplication.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.apiReplication.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.realTimeBilling.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.realTimeBilling.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.botVerification.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.botVerification.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.botActivation.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.botActivation.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.searchVisibility.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.searchVisibility.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.botParking.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.botParking.description') }}</p>
          </div>
          <div class="feature-card">
            <h3>{{ $t('cascadeApi.advancedFeatures.trafficLimiting.title') }}</h3>
            <p>{{ $t('cascadeApi.advancedFeatures.trafficLimiting.description') }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Pricing Section -->
    <div class="page-section bg-gray-50">
      <div class="section-content">
        <div class="section-title">
          {{ $t('cascadeApi.pricing.title') }}
        </div>
        <div class="pricing-content">
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>{{ $t('cascadeApi.pricing.basic.title') }}</h3>
              <div class="price">{{ $t('cascadeApi.pricing.basic.price') }}</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.basic.feature1') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.basic.feature2') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.basic.feature3') }}</div>
              </div>
            </div>
          </div>
          <div class="pricing-card featured">
            <div class="pricing-header">
              <h3>{{ $t('cascadeApi.pricing.professional.title') }}</h3>
              <div class="price">{{ $t('cascadeApi.pricing.professional.price') }}</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.professional.feature1') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.professional.feature2') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.professional.feature3') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.professional.feature4') }}</div>
              </div>
            </div>
          </div>
          <div class="pricing-card">
            <div class="pricing-header">
              <h3>{{ $t('cascadeApi.pricing.enterprise.title') }}</h3>
              <div class="price">{{ $t('cascadeApi.pricing.enterprise.price') }}</div>
            </div>
            <div class="pricing-features">
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.enterprise.feature1') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.enterprise.feature2') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.enterprise.feature3') }}</div>
              </div>
              <div class="feature-item">
                <div class="feature-icon">✓</div>
                <div class="feature-text">{{ $t('cascadeApi.pricing.enterprise.feature4') }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="processing-cost">
          <h3>{{ $t('cascadeApi.pricing.processingCost') }}</h3>
        </div>
      </div>
    </div>

    <!-- CTA Section -->
    <div class="page-section bg-gradient bottom-banner">
      <div class="container">
        <div class="section-content text-center">
          <div class="cta-content">
            <h2 class="section-head-title mb-4">{{ $t('cascadeApi.cta.title') }}</h2>
            <p class="text-gray-600 mb-8">{{ $t('cascadeApi.cta.description') }}</p>
            <div class="banner-action flex flex-wrap items-center justify-center gap-4">
              <a href="https://omni.smsbat.com/register-ca" target="_blank" class="nav-link button filled">
                <p>{{ $t('links.registerBusinessAccount') }}</p>
              </a>
              <NuxtLink to="https://developers.smsbat.com/" external target="_blank" class="button outlined">
                <p>{{ $t('cascadeApi.cta.viewDocs') }}</p>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped lang="scss">
.smsbat-cascade-restful-api-page {
  padding-top: 100px;
}

.main-banner {
  padding: 120px 0 80px 0;
  min-height: 80vh;
  display: flex;
  align-items: center;
}

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.banner-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
}

.banner-text {
  flex: 1;
}

.banner-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 20px;
  line-height: 1.2;
  word-wrap: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

.banner-description {
  font-size: 1.2rem;
  opacity: 0.9;
  line-height: 1.6;
  margin-bottom: 30px;
}

.banner-action {
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  margin-top: 30px;
}

.banner-action .button {
  min-width: fit-content;
  white-space: nowrap;
  padding: 0 24px;
}

.banner-image-container {
  flex: 1;
  text-align: center;
}

.page-section {
  padding: 80px 0;
}

.section-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.section-title {
  font-size: 2.5rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 60px;
  color: #333;
}

.stats-container {
  text-align: center;
  background: #f8f9fa;
  padding: 60px 40px;
  border-radius: 20px;
  width: 100%;
  margin: 0;
}

.stats-title {
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

.stats-subtitle {
  font-size: 1.1rem;
  color: #666;
  margin-bottom: 40px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 3rem;
  font-weight: bold;
  color: #3070b6;
  margin-bottom: 10px;
}

.stat-label {
  font-size: 1rem;
  color: #666;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.feature-item:hover {
  transform: translateY(-5px);
}

.feature-icon {
  flex-shrink: 0;
}

.feature-content h3 {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.feature-content p {
  color: #666;
  line-height: 1.6;
}

.bg-gray-50 {
  background-color: #f9fafb;
}

.channels-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.channel-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 25px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 3px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.channel-item:hover {
  transform: translateY(-3px);
}

.channel-icon {
  flex-shrink: 0;
}

.channel-content h3 {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.channel-content p {
  color: #666;
  line-height: 1.5;
}

.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
}

.benefit-item {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.benefit-item:hover {
  transform: translateY(-5px);
}

.benefit-icon {
  font-size: 2rem;
  flex-shrink: 0;
}

.benefit-content h3 {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.benefit-content p {
  color: #666;
  line-height: 1.6;
}

.how-it-works-content {
  max-width: 800px;
  margin: 0 auto;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 40px;
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.step-number {
  width: 60px;
  height: 60px;
  background: #3070b6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  font-weight: bold;
  flex-shrink: 0;
}

.step-content h3 {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.step-content p {
  color: #666;
  line-height: 1.6;
}

.advanced-features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
}

.feature-card {
  padding: 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-5px);
}

.feature-card h3 {
  font-size: 1.3rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.feature-card p {
  color: #666;
  line-height: 1.6;
}

.pricing-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  max-width: 1000px;
  margin: 0 auto;
}

.pricing-card {
  padding: 40px 30px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  position: relative;
}

.pricing-card:hover {
  transform: translateY(-5px);
}

.pricing-card.featured {
  border: 2px solid #3070b6;
  transform: scale(1.05);
}

.pricing-card.featured:hover {
  transform: scale(1.05) translateY(-5px);
}

.pricing-header {
  text-align: center;
  margin-bottom: 30px;
}

.pricing-header h3 {
  font-size: 1.5rem;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
}

.price {
  font-size: 2.5rem;
  font-weight: bold;
  color: #3070b6;
}

.pricing-features .feature-item {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
  padding: 0;
  background: none;
  box-shadow: none;
  text-align: left;
}

.pricing-features .feature-item:hover {
  transform: none;
}

.pricing-features .feature-icon {
  width: 20px;
  height: 20px;
  background: #3070b6;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  flex-shrink: 0;
}

.pricing-features .feature-text {
  font-size: 1rem;
  color: #333;
}

.cta-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

.cta-description {
  font-size: 1.2rem;
  color: #666;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.cta-actions {
  display: flex;
  gap: 20px;
  justify-content: center;
  align-items: center;
}

.nav-link {
  text-decoration: none;
}

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 20px;
  height: 52px;
  font-size: 18px;
  font-weight: 800;
  border: 2px solid #3070b6;
  border-radius: 10px;
  transition: 0.2s ease;
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
}

.button.filled {
  color: #fff;
  background: #3070b6;
}

.button.filled:hover {
  color: #3070b6;
  background: #fff;
}

.button.outlined {
  color: #3070b6;
  background: #fff;
}

.button.outlined:hover {
  color: #fff;
  background: #3070b6;
}

.processing-cost {
  text-align: center;
  margin-top: 40px;
  padding: 20px;
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
}

.processing-cost h3 {
  font-size: 1.5rem;
  font-weight: bold;
  color: #3070b6;
  margin: 0;
}

.bg-gradient {
  background: linear-gradient(135deg, #4f46e5 0%, #764ba2 100%);
  color: white;
}

.bg-gradient .section-head-title {
  color: white;
}

.bg-gradient .text-gray-600 {
  color: rgba(255, 255, 255, 0.8);
}

.bottom-banner {
  padding: 80px 0;
}

.cta-content {
  max-width: 800px;
  margin: 0 auto;
}

.section-head-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 20px;
  color: #333;
}

@media (max-width: 992px) {
  .main-banner {
    padding: 0 0 80px 0;
  }

  .smsbat-cascade-restful-api-page {
    padding-top: 0;
  }
}

@media (max-width: 768px) {
  .banner-content {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .banner-title {
    font-size: 2rem;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }

  .section-title {
    font-size: 2rem;
  }

  .features-grid {
    grid-template-columns: 1fr;
  }

  .channels-grid {
    grid-template-columns: 1fr;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
  }

  .step-item {
    flex-direction: column;
    text-align: center;
  }

  .advanced-features-grid {
    grid-template-columns: 1fr;
  }

  .pricing-content {
    grid-template-columns: 1fr;
  }

  .pricing-card.featured {
    transform: none;
  }

  .pricing-card.featured:hover {
    transform: translateY(-5px);
  }

  .cta-actions {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .container {
    padding: 0 10px;
  }

  .banner-content {
    padding: 0 10px;
  }

  .banner-title {
    font-size: 1.6rem;
  }

  .banner-description {
    font-size: 1rem;
  }
}
</style>
